<template>
  <div class="main">
    <div v-if="isLoggedIn || $route.path === '/unauthorized'" class="maincontent">
      <transition name="slide" v-if="isLoggedIn">
        <div class="navigation-menu" v-if="showNavigationMenu">
          <NavigationMenu v-show="!collapsed" />
          <CollapseButton @collapse="toggleCollapse" :collapsed="collapsed" />
        </div>
      </transition>
      <div class="content slds-box slds-theme_default">
        <Toast/>
        <NotificationsManager />
        <transition name="fade">
          <router-view/>
        </transition>
      </div>
    </div>
    <div v-else class="maincontent">
      <Spinner />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { subscribeToCampaignsNotifications } from '../components/utils/socketUtils'
import jwt from 'jsonwebtoken'
import moment from 'moment'
import CollapseButton from '../components/CollapseButton'
import NavigationMenu from '../components/NavigationMenu'
import NotificationsManager from '../components/slds/NotificationsManager'
import Spinner from '../components/Spinner'
import Toast from '../components/utils/Toast.vue'
import { clear, dropDb, getDb, insert } from '../components/utils/indexedDbUtils'

export default {
  components: {
    CollapseButton,
    NavigationMenu,
    NotificationsManager,
    Toast,
    Spinner
  },
  data: function () {
    return {
      client: undefined,
      collapsed: false
    }
  },
  async created () {
    await this.login()
    const client = await this.subscribeToNotifications()
    this.client = client
  },
  beforeDestroy () {
    if (this.client) {
      this.client.disconnect()
    }
  },
  methods: {
    async doLogout () {
      await dropDb()
      localStorage.removeItem('ACCESS_TOKEN')
      localStorage.removeItem('EMPTY_CONFIG_DATE')
      localStorage.removeItem('MODELS_DATE')
      //localStorage.removeItem('MVS_DATE')
      //localStorage.removeItem('DEALERS_MAP_DATE')
    },
    async login () {
      if (this.$route.path === '/campaigns') {
        await this.doLogout()
      }
      const code = this.$route.query.code
      const accessToken = localStorage.getItem('ACCESS_TOKEN')

      if (accessToken && accessToken !== 'undefined') {
        const decodedJwt = jwt.decode(accessToken)

        if (decodedJwt.exp * 1000 > new Date().getTime()) {
          this.$store.dispatch('session/setAccessToken', accessToken)
          await this.$store.dispatch('session/fetchUserProfile', this)
          await this.getConfigAndMvs()
        } else if (code) {
          await this.loginWithCode(code)
        } else {
          await this.$store.dispatch('session/login')
        }
      } else if (code) {
        await this.loginWithCode(code)
      } else {
        await this.$store.dispatch('session/login')
      }
    },
    async loginWithCode (code) {
      await this.$store.dispatch('session/doSSO', code)
      try {
        await this.$store.dispatch('session/fetchUserProfile', this)
        await this.getConfigAndMvs()
        this.$i18n.locale = this.$store.getters['session/locale']
      } catch (error) {
        console.error(error)
        this.$router.push('/unauthorized')
      }
    },
    async getConfig (db) {
      const config = await this.$store.dispatch('campaignDesigner/getEmptyConfig')
      clear(db, 'config')

      Object.entries(config).forEach(([key, value]) => {
        insert(db, 'config', { key, config: value })
      })

      // insert(db, 'config', { key: 'FULL', config })
      // insert(db, 'config', { key: 'HEADER', config: _.omit(config, ['filters', 'contents', 'offers', 'preview']) })

      localStorage.setItem('EMPTY_CONFIG_DATE', moment().format('YYYY-MM-DD'))
    },
    async getDealersMap (db) { // DEALER CLUSTER REMOVED 23/07/2024
      const dealers = await this.$store.dispatch('campaignDesigner/getDealersMap')

      if (dealers) {
        const clustersMap = Object.keys(dealers).map(cluster => { return { ...dealers[cluster], cluster } })
        const dealersList = []

        Object.keys(dealers).map(cluster => {
          dealers[cluster].dealers.forEach(dealer => {
            if (!dealersList.find(d => d.idDealer === dealer.idDealer)) {
              dealersList.push(dealer)
            }
          })
        })

        clear(db, 'clusters')
        clear(db, 'dealers')
        insert(db, 'clusters', clustersMap, true)
        insert(db, 'dealers', dealersList, true)

        localStorage.setItem('DEALERS_MAP_DATE', moment().format('YYYY-MM-DD'))
      }
    },
    async getModels (db) {
      const models = await this.$store.dispatch('campaignDesigner/getModels')

      const modelsMap = []

      if (models) {
        Object.keys(models).forEach(brand => {
          models[brand].forEach(element => {
            modelsMap.push({ ...element, brand })
          })
        })
      }

      clear(db, 'models')
      insert(db, 'models', modelsMap, true)

      localStorage.setItem('MODELS_DATE', moment().format('YYYY-MM-DD'))
    },
    async getMvs (db) { // INACTIVE 23/07/2024
      const mvs = await this.$store.dispatch('campaignDesigner/getMvs')

      const mvsMap = []

      if (mvs) {
        Object.keys(mvs).forEach(brand => {
          mvs[brand].forEach(element => {
            mvsMap.push({ ...element, brand })
          })
        })
      }

      clear(db, 'mvs')
      insert(db, 'mvs', mvsMap, true)

      localStorage.setItem('MVS_DATE', moment().format('YYYY-MM-DD'))
    },
    async getConfigAndMvs () {
      const db = await getDb()
      if (!localStorage.getItem('EMPTY_CONFIG_DATE') || moment().isAfter(localStorage.getItem('EMPTY_CONFIG_DATE'), 'day')) {
        await this.getConfig(db)
      }

      // if (!localStorage.getItem('MODELS_DATE') || moment().isAfter(localStorage.getItem('MODELS_DATE'), 'day')) {
      //   await this.getModels(db)
      // }

      /*REMOVED MVS(INACTIVE) AND DEALER(DEALER CLUSTER RMEOVED) 23/07/2024
      if (!localStorage.getItem('MVS_DATE') || moment().isAfter(localStorage.getItem('MVS_DATE'), 'day')) {
        await this.getMvs(db)
      }

      if (!localStorage.getItem('DEALERS_DATE') || moment().isAfter(localStorage.getItem('DEALERS_DATE'), 'day')) {
        await this.getDealersMap(db)
      }*/
    },
    async subscribeToNotifications () {
      const visibilities = this.$store.getters['session/userVisibilities']
      subscribeToCampaignsNotifications(visibilities, (msg) => {
        const notification = JSON.parse(msg)
        const { icon, title, text, params, url, fixed } = notification

        const textToShow = this.$t(`notification.${text}`, params)
        this.showNotification(icon, this.$t(`notification.${title}`, params), textToShow, url, fixed)
      })
    },
    toggleCollapse () {
      this.collapsed = !this.collapsed
    }
  },
  computed: {
    showNavigationMenu () {
      const paths = ['*', '/logout', '/unauthorized', '/campaign-not-found']
      return !!this.$store.getters['session/userProfile'] && this.$route.matched.every(route => !paths.includes(route.path))
    },
    isLoggedIn () {
      return this.$store.getters['session/accessToken'] !== undefined // && !!this.client
    }
  }
}
</script>

<style lang="scss">
.main {
  display: flex;
  flex: 1;
  flex-direction: row;
}

.maincontent {
  display: flex;
  flex: 1;
  flex-direction: row;
}

.navigation-menu {
  flex-grow: 0;
  display: flex;
}

.content {
  flex-grow: 2;
  max-height: 100vh;
  overflow-y: auto;
}

// transition
.slide-enter-active, .slide-leave-active {
  transition: transform 0.2s ease;
}

.slide-enter, .slide-leave-to {
  transform: translateX(-100%);
  transition: all 300ms linear 0s
}
</style>
